import { XHRError } from "@/mk2/helpers/api";
import { GetParams, serializeGetParams } from "@/mk2/helpers/urls";

export interface XHRApiAuth {
    headers: Record<string, string>;
    baseUrl: string;
    verifier: () => void;
}

export interface XHRResponse extends Response {
    // Originally we were using superagent, which parses JSON response
    // automatically and returns it in "response.body" property.
    //
    // To minimize changes of existing codebase, we are doing the same,
    // parsing JSON response and putting it in "body" property
    // of returned response.
    body: any;
}

export async function makeRequest(
    apiAuth: XHRApiAuth,
    method: "GET" | "POST",
    url: string,
    data?: any,
): Promise<XHRResponse> {
    apiAuth.verifier(); // throws if problem

    let fullUrl = apiAuth.baseUrl + url;
    const headers: Record<string, string> = {
        // used by Django to detect AJAX request (request.is_ajax() is True)
        "X-Requested-With": "XMLHttpRequest",
        ...apiAuth.headers,
    };
    const init: RequestInit = {
        method,
        headers,
        // tells fetch() to send cookies with the request,
        // even for cross-origin requests
        credentials: "include",
    };

    if (data) {
        if (method === "GET") {
            const qs = serializeGetParams(data as GetParams);
            if (qs) {
                fullUrl += (fullUrl.includes("?") ? "&" : "?") + qs;
            }
        } else {
            headers["Content-Type"] = "application/json";
            init.body = JSON.stringify(data);
        }
    }

    let fetchResponse: Response;
    try {
        fetchResponse = await fetch(fullUrl, init);
    } catch (networkErr) {
        // Network error (CORS, offline, DNS failure, etc.)
        const err = new Error(
            networkErr instanceof Error ? networkErr.message : "Network request failed",
        ) as XHRError<any>;
        err.status = 400;
        err.errorCode = "CROSS_DOMAIN_ERROR";
        err.response = { body: {} };
        throw err;
    }

    const xhrResponse: XHRResponse = { ...fetchResponse } as XHRResponse;
    try {
        const body: any = await fetchResponse.json();
        xhrResponse.body = body;
    } catch (jsonErr) {
        xhrResponse.body = {};

        if (fetchResponse.ok) {
            const err = new Error(
                jsonErr instanceof Error ? jsonErr.message : "Failed to parse JSON response",
            ) as XHRError<any>;
            err.status = 500;
            err.errorCode = "JSON_PARSE_ERROR";
            err.response = xhrResponse;
            throw err;
        }
    }

    if (!fetchResponse.ok) {
        const err: any = new Error(`${xhrResponse.status} ${xhrResponse.statusText}`);
        err.response = xhrResponse;
        throw err;
    }

    return xhrResponse;
}

const DEFAULT_NUMBER_OF_RETRIES = 1;

/**
 * Try to make request - if it fails with crossDomain error, retry it
 *
 * @param apiAuth
 * @param method {string}
 * @param url {string}
 * @param data {object}
 */
export async function makeRequestWithRetries(
    apiAuth: XHRApiAuth,
    method: "GET",
    url: string,
    data?: any,
): Promise<XHRResponse> {
    const numOfRetries = DEFAULT_NUMBER_OF_RETRIES;

    let i = 0;
    while (true) {
        try {
            const res = await makeRequest(apiAuth, method, url, data);
            return res;
        } catch (err) {
            if (err.crossDomain && i < numOfRetries) {
                i++;

                // Retry request
                continue;
            }

            throw err; // Re-throw
        }
    }
}
